/**
 * Backend error code → user-facing presentation mapping.
 *
 * Every code the backend can return on a mutation should have an entry here.
 * `mapApiError` (in `api-error.ts`) reads this table to decide what the user
 * sees: a toast, a modal, or "silent" (caller handles inline — e.g. a form
 * field error). Unknown codes fall through to the generic toast in the
 * mapper.
 *
 * Keep entries grouped by backend module so adding a new endpoint = one
 * obvious place to update. If you add a NEW backend code, add it here too.
 */

export type ErrorSeverity = "error" | "warning" | "info";

export interface ToastConfig {
  kind: "toast";
  severity: ErrorSeverity;
  message: string;
}

export interface ModalConfig {
  kind: "modal";
  severity: ErrorSeverity;
  title: string;
  message: string;
  /** Optional primary action (e.g. a "Buy a plan" button → "/billing"). */
  action?: { label: string; href: string };
}

/**
 * "Silent" tells the dispatcher to do nothing — the caller is expected to
 * surface this code itself (typically as an inline form-field error).
 */
export interface SilentConfig {
  kind: "silent";
  /** Human-readable hint that ends up in the dev console if no caller handles it. */
  hint: string;
}

export type ErrorPresentation = ToastConfig | ModalConfig | SilentConfig;

/**
 * Code → presentation. Keys are uppercase SNAKE_CASE matching backend
 * `serviceError(status, code, message)` codes verbatim.
 */
export const ERROR_REGISTRY: Record<string, ErrorPresentation> = {
  // ── Auth / session ───────────────────────────────────────────────────────
  AUTH_INVALID: {
    kind: "silent",
    hint: "Invalid credentials — caller shows inline form error",
  },
  WRONG_PASSWORD: {
    kind: "silent",
    hint: "Wrong password — caller shows inline form error",
  },
  FORBIDDEN: { kind: "toast", severity: "error", message: "errors:FORBIDDEN.message" },
  NO_MERCHANT: {
    kind: "toast",
    severity: "error",
    message: "errors:NO_MERCHANT.message",
  },
  CANNOT_MODIFY_OWNER: {
    kind: "toast",
    severity: "warning",
    message: "errors:CANNOT_MODIFY_OWNER.message",
  },
  CANNOT_MODIFY_SELF: {
    kind: "toast",
    severity: "warning",
    message: "errors:CANNOT_MODIFY_SELF.message",
  },
  CANNOT_REMOVE_OWNER: {
    kind: "toast",
    severity: "warning",
    message: "errors:CANNOT_REMOVE_OWNER.message",
  },
  CANNOT_REMOVE_SELF: {
    kind: "toast",
    severity: "warning",
    message: "errors:CANNOT_REMOVE_SELF.message",
  },

  // ── Subscription / billing gates ─────────────────────────────────────────
  SUBSCRIPTION_EXPIRED: {
    kind: "modal",
    severity: "error",
    title: "errors:SUBSCRIPTION_EXPIRED.title",
    message: "errors:SUBSCRIPTION_EXPIRED.message",
    action: { label: "errors:SUBSCRIPTION_EXPIRED.actionLabel", href: "/billing" },
  },
  SEAT_CAP_REACHED: {
    kind: "silent",
    hint: "Invitation seat cap — caller shows inline form error",
  },
  ADDON_REQUIRES_PAID_PLAN: {
    kind: "toast",
    severity: "warning",
    message: "errors:ADDON_REQUIRES_PAID_PLAN.message",
  },
  ADDON_QTY_DECREASE_NOT_ALLOWED: {
    kind: "toast",
    severity: "warning",
    message: "errors:ADDON_QTY_DECREASE_NOT_ALLOWED.message",
  },
  NO_REMAINING_TERM: {
    kind: "toast",
    severity: "warning",
    message: "errors:NO_REMAINING_TERM.message",
  },
  IDEMPOTENCY_KEY_MISMATCH: {
    kind: "toast",
    severity: "warning",
    message: "errors:IDEMPOTENCY_KEY_MISMATCH.message",
  },
  TOO_MANY_PENDING_ORDERS: {
    kind: "modal",
    severity: "warning",
    title: "errors:TOO_MANY_PENDING_ORDERS.title",
    message: "errors:TOO_MANY_PENDING_ORDERS.message",
    action: { label: "errors:TOO_MANY_PENDING_ORDERS.actionLabel", href: "/billing/orders" },
  },
  PENDING_ORDER_EXISTS: {
    kind: "modal",
    severity: "warning",
    title: "errors:PENDING_ORDER_EXISTS.title",
    message: "errors:PENDING_ORDER_EXISTS.message",
    action: { label: "errors:PENDING_ORDER_EXISTS.actionLabel", href: "/billing/orders" },
  },
  PERSIST_FAILED: {
    kind: "modal",
    severity: "error",
    title: "errors:PERSIST_FAILED.title",
    message: "errors:PERSIST_FAILED.message",
  },
  PAYMENT_PROVIDER_ERROR: {
    kind: "toast",
    severity: "error",
    message: "errors:PAYMENT_PROVIDER_ERROR.message",
  },
  INVALID_TOTAL: {
    kind: "toast",
    severity: "error",
    message: "errors:INVALID_TOTAL.message",
  },
  RECEIPT_PROFILE_REQUIRED: {
    kind: "toast",
    severity: "warning",
    message: "errors:RECEIPT_PROFILE_REQUIRED.message",
  },

  // ── Media upload (rate-limit / quota / validation) ───────────────────────
  RATE_LIMITED: {
    kind: "toast",
    severity: "warning",
    message: "errors:RATE_LIMITED.message",
  },
  QUOTA_EXCEEDED: {
    kind: "modal",
    severity: "warning",
    title: "errors:QUOTA_EXCEEDED.title",
    message: "errors:QUOTA_EXCEEDED.message",
    action: { label: "errors:QUOTA_EXCEEDED.actionLabel", href: "/billing" },
  },
  INVALID_FILE_TYPE: {
    kind: "toast",
    severity: "error",
    message: "errors:INVALID_FILE_TYPE.message",
  },
  PAYLOAD_TOO_LARGE: {
    kind: "toast",
    severity: "error",
    message: "errors:PAYLOAD_TOO_LARGE.message",
  },
  IMAGE_DIMENSIONS_TOO_LARGE: {
    kind: "toast",
    severity: "error",
    message: "errors:IMAGE_DIMENSIONS_TOO_LARGE.message",
  },
  UPLOAD_FAILED: {
    kind: "toast",
    severity: "error",
    message: "errors:UPLOAD_FAILED.message",
  },

  // ── Channel connect ──────────────────────────────────────────────────────
  MERCHANT_CHANNEL_LIMIT: {
    kind: "toast",
    severity: "warning",
    message: "errors:MERCHANT_CHANNEL_LIMIT.message",
  },
  SELECT_ONE_PAGE_FIRST: {
    kind: "toast",
    severity: "warning",
    message: "errors:SELECT_ONE_PAGE_FIRST.message",
  },
  SELECT_ONE_ACCOUNT_FIRST: {
    kind: "toast",
    severity: "warning",
    message: "errors:SELECT_ONE_ACCOUNT_FIRST.message",
  },
  LINE_TOKEN_INVALID: {
    kind: "toast",
    severity: "error",
    message: "errors:LINE_TOKEN_INVALID.message",
  },
  MISSING_ACCESS_TOKEN: {
    kind: "toast",
    severity: "error",
    message: "errors:MISSING_ACCESS_TOKEN.message",
  },
  FB_NO_PAGES: {
    kind: "toast",
    severity: "warning",
    message: "errors:FB_NO_PAGES.message",
  },
  IG_NO_ACCOUNTS: {
    kind: "toast",
    severity: "warning",
    message: "errors:IG_NO_ACCOUNTS.message",
  },
  INVALID_STATE: {
    kind: "toast",
    severity: "error",
    message: "errors:INVALID_STATE.message",
  },
  UNSUPPORTED_PLATFORM: {
    kind: "toast",
    severity: "error",
    message: "errors:UNSUPPORTED_PLATFORM.message",
  },
  CHANNEL_NOT_FOUND: {
    kind: "toast",
    severity: "error",
    message: "errors:CHANNEL_NOT_FOUND.message",
  },
  CHANNEL_REAUTH_REQUIRED: {
    kind: "toast",
    severity: "error",
    message: "errors:CHANNEL_REAUTH_REQUIRED.message",
  },
  CHANNEL_INACTIVE: {
    kind: "toast",
    severity: "error",
    message: "errors:CHANNEL_INACTIVE.message",
  },
  TOKEN_CHECK_FAILED: {
    kind: "toast",
    severity: "warning",
    message: "errors:TOKEN_CHECK_FAILED.message",
  },
  PAGE_ALREADY_OWNED_BY_OTHER_MERCHANT: {
    kind: "toast",
    severity: "error",
    message: "errors:PAGE_ALREADY_OWNED_BY_OTHER_MERCHANT.message",
  },
  INVALID_CATEGORY_ID: {
    kind: "toast",
    severity: "error",
    message: "errors:INVALID_CATEGORY_ID.message",
  },
  FB_TOKEN_INVALID: {
    kind: "toast",
    severity: "error",
    message: "errors:FB_TOKEN_INVALID.message",
  },
  IG_TOKEN_INVALID: {
    kind: "toast",
    severity: "error",
    message: "errors:IG_TOKEN_INVALID.message",
  },
  // Meta's 24h customer-initiated window blocked this send. Token is
  // fine — the customer just needs to send a new message first before
  // we can reach them. Warning (not error) because the merchant action
  // is "wait for the customer" rather than "fix something broken".
  FB_POLICY_BLOCKED: {
    kind: "toast",
    severity: "warning",
    message: "errors:FB_POLICY_BLOCKED.message",
  },
  IG_POLICY_BLOCKED: {
    kind: "toast",
    severity: "warning",
    message: "errors:IG_POLICY_BLOCKED.message",
  },
  FB_BAD_RESPONSE: {
    kind: "toast",
    severity: "error",
    message: "errors:FB_BAD_RESPONSE.message",
  },
  IG_BAD_RESPONSE: {
    kind: "toast",
    severity: "error",
    message: "errors:IG_BAD_RESPONSE.message",
  },
  LINE_API_ERROR: {
    kind: "toast",
    severity: "error",
    message: "errors:LINE_API_ERROR.message",
  },
  LINE_BAD_RESPONSE: {
    kind: "toast",
    severity: "error",
    message: "errors:LINE_BAD_RESPONSE.message",
  },
  LINE_QUOTA_EXHAUSTED: {
    kind: "toast",
    severity: "error",
    message: "errors:LINE_QUOTA_EXHAUSTED.message",
  },
  TIKTOK_BAD_RESPONSE: {
    kind: "toast",
    severity: "error",
    message: "errors:TIKTOK_BAD_RESPONSE.message",
  },

  // ── Invitations / team ──────────────────────────────────────────────────
  ALREADY_ACCEPTED: {
    kind: "toast",
    severity: "info",
    message: "errors:ALREADY_ACCEPTED.message",
  },
  INVITATION_NOT_FOUND: {
    kind: "toast",
    severity: "error",
    message: "errors:INVITATION_NOT_FOUND.message",
  },
  INVITATION_ALREADY_CLAIMED: {
    kind: "toast",
    severity: "warning",
    message: "errors:INVITATION_ALREADY_CLAIMED.message",
  },
  EMAIL_MISMATCH: {
    kind: "toast",
    severity: "error",
    message: "errors:EMAIL_MISMATCH.message",
  },
  USER_ALREADY_MEMBER: {
    kind: "toast",
    severity: "info",
    message: "errors:USER_ALREADY_MEMBER.message",
  },
  SELF_INVITE: {
    kind: "toast",
    severity: "warning",
    message: "errors:SELF_INVITE.message",
  },
  INVITE_COOLDOWN: {
    kind: "toast",
    severity: "warning",
    message: "errors:INVITE_COOLDOWN.message",
  },

  // ── Products / SKU / orders ──────────────────────────────────────────────
  PRODUCT_NOT_FOUND: { kind: "toast", severity: "error", message: "errors:PRODUCT_NOT_FOUND.message" },
  SKU_NOT_FOUND: { kind: "toast", severity: "error", message: "errors:SKU_NOT_FOUND.message" },
  SKU_INACTIVE: {
    kind: "toast",
    severity: "warning",
    message: "errors:SKU_INACTIVE.message",
  },
  SKU_ALREADY_EXISTS: {
    kind: "toast",
    severity: "warning",
    message: "errors:SKU_ALREADY_EXISTS.message",
  },
  CUSTOMER_NOT_FOUND: { kind: "toast", severity: "error", message: "errors:CUSTOMER_NOT_FOUND.message" },
  ORDER_NOT_FOUND: { kind: "toast", severity: "error", message: "errors:ORDER_NOT_FOUND.message" },
  ORDER_ALREADY_PAID: {
    kind: "toast",
    severity: "info",
    message: "errors:ORDER_ALREADY_PAID.message",
  },
  INVALID_TRANSITION: {
    kind: "toast",
    severity: "warning",
    message: "errors:INVALID_TRANSITION.message",
  },
  CONVERSATION_NOT_FOUND: {
    kind: "toast",
    severity: "error",
    message: "errors:CONVERSATION_NOT_FOUND.message",
  },
  CONVERSATION_CUSTOMER_MISMATCH: {
    kind: "toast",
    severity: "error",
    message: "errors:CONVERSATION_CUSTOMER_MISMATCH.message",
  },

  // ── AI bot / commerce (bot-action approve & reject → CommerceGate) ────────
  // Each carries a distinct meaning that drives a different merchant action
  // (re-propose vs. restock vs. nothing), so these must show tailored copy —
  // `useBotActions` intentionally does NOT pass a `fallbackMessage`, letting
  // these registry entries win.
  BOT_ACTION_EXPIRED: {
    kind: "toast",
    severity: "warning",
    message: "errors:BOT_ACTION_EXPIRED.message",
  },
  BOT_ACTION_NOT_PENDING: {
    kind: "toast",
    severity: "info",
    message: "errors:BOT_ACTION_NOT_PENDING.message",
  },
  BOT_ACTION_ALREADY_EXECUTED: {
    kind: "toast",
    severity: "info",
    message: "errors:BOT_ACTION_ALREADY_EXECUTED.message",
  },
  BOT_ACTION_NOT_FOUND: {
    kind: "toast",
    severity: "warning",
    message: "errors:BOT_ACTION_NOT_FOUND.message",
  },
  BOT_ACTION_NO_CUSTOMER: {
    kind: "toast",
    severity: "error",
    message: "errors:BOT_ACTION_NO_CUSTOMER.message",
  },
  AI_CONFIG_NOT_FOUND: {
    kind: "toast",
    severity: "error",
    message: "errors:AI_CONFIG_NOT_FOUND.message",
  },
  INSUFFICIENT_STOCK: {
    kind: "toast",
    severity: "warning",
    message: "errors:INSUFFICIENT_STOCK.message",
  },

  // ── Consent ──────────────────────────────────────────────────────────────
  POLICY_NOT_FOUND: {
    kind: "toast",
    severity: "error",
    message: "errors:POLICY_NOT_FOUND.message",
  },

  // ── User-state edge cases ────────────────────────────────────────────────
  USER_NOT_FOUND: { kind: "toast", severity: "error", message: "errors:USER_NOT_FOUND.message" },
  // ── Server / provider failures ───────────────────────────────────────────
  EMAIL_PROVIDER_UNREACHABLE: {
    kind: "toast",
    severity: "error",
    message: "errors:EMAIL_PROVIDER_UNREACHABLE.message",
  },
  EMAIL_NOT_CONFIGURED: {
    kind: "toast",
    severity: "error",
    message: "errors:EMAIL_NOT_CONFIGURED.message",
  },
  EMAIL_TOO_LARGE: {
    kind: "toast",
    severity: "error",
    message: "errors:EMAIL_TOO_LARGE.message",
  },
};

/**
 * Generic fallback for any code not in the registry. Used by `mapApiError` so
 * the user always sees *something* even if a brand-new backend code lands.
 */
export const FALLBACK_PRESENTATION: ToastConfig = {
  kind: "toast",
  severity: "error",
  message: "errors:FALLBACK.message",
};
